// @flow

import type { Endpoints, EntityFieldCollection, SortParams } from '../types';
import { createdAtField, getCreatedAtSort, idField, paymentModeField } from '../abstract/StoredEntity';
import type { StoredEntity } from '../abstract/StoredEntity';
import { constructEndpoints } from '../helpers';
import User from './User';

class PaymentMethod implements StoredEntity {
    static classInternalName = 'payment_method';

    static fields: EntityFieldCollection = {
        id: idField,
        createdAt: createdAtField,
        name: {
            displayName: 'Name',
            recordName: 'name',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: true,
            displayOnMain: false,
        },
        number: {
            displayName: 'Number',
            recordName: 'number',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        expiration: {
            displayName: 'Expiration',
            recordName: 'expiration',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: false,
        },
        network: {
            displayName: 'Payment Network',
            recordName: 'network',
            dataType: {
                primitive: 'enum',
                isArray: false,
                extraData: {
                    1: 'Visa',
                    2: 'MasterCard',
                    3: 'American Express',
                    4: 'Diners Club',
                },
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        enabled: {
            displayName: 'Enabled',
            recordName: 'enabled',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        isExpired: {
            displayName: 'Expired',
            recordName: 'is_expired',
            dataType: {
                primitive: 'bool',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        userId: {
            displayName: 'User',
            recordName: 'user_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    model: () => User,
                    maxValues: 1,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: undefined,
            canEdit: undefined,
            isOptional: false,
            displayOnMain: true,
        },
        paymentMode: paymentModeField,
    };

    static fileFields: EntityFieldCollection = {};

    static defaultSort: SortParams = getCreatedAtSort('created_at');

    get id() {
        return this.record[PaymentMethod.fields.id.recordName];
    }

    get displayName() {
        return this.record[PaymentMethod.fields.number.recordName];
    }

    static endpoints: Endpoints = constructEndpoints('payment-methods');

    constructor(record: any) {
        this.record = record;
    }
}

export default PaymentMethod;
